import React, { cloneElement } from "react";
import { StandardPageWrapper } from "../../../components/layout/StandardPageWrapper";
import { PageHeader } from "../../../components/layout/header/Header";
import { DashboardCompletionStepBox } from "@truevault/aquilo";
import { Routes } from "../../../root/routes";
import { usePrimaryOrganizationId } from "../../../hooks";
import {
  areHrNoticesComplete,
  isPrivacyCenterOnboardingComplete,
  isWorkflowsOnboardingComplete,
  TruevaultHrOnboardingProgress,
  TruevaultOnboardingProgress,
  useOnboardingProgress,
} from "../../../../common/hooks/useOnboardingProgress";
import { UserRole } from "../../../../common/service/server/Types";

export const DevInstructionsFinishedHrDataMapSlug = "finished-hr-data-map";
export const DevInstructionsFinishedHrDsarWorkflowsSlug = "finished-hr-dsar-workflows";

// HR Center is enabled after both Privacy Center and DSAR Workflows onboarding are complete (or if any of the HR onboarding steps are already complete)
export const isHrCenterEnabled = (
  onboardingProgress: TruevaultOnboardingProgress,
  loggedInUserRole: UserRole,
) => {
  // Always allow Admins to access HR Center (since HR Users can also always access the HR Center)
  if (loggedInUserRole === "ADMIN") {
    return true;
  }

  let isAnyHrStepComplete =
    onboardingProgress?.hrCenterOnboardingProgress?.isAllHrDataMapCompleted ||
    onboardingProgress?.hrCenterOnboardingProgress?.isFinishedWithHrRetentionPolicy ||
    onboardingProgress?.hrCenterOnboardingProgress?.isFinishedWithHrDsarWorkflows ||
    onboardingProgress?.hrCenterOnboardingProgress?.isHrNoticesPublished;
  if (isAnyHrStepComplete) {
    return true;
  }

  return (
    isPrivacyCenterOnboardingComplete(onboardingProgress.privacyCenterOnboardingProgress) &&
    isWorkflowsOnboardingComplete(onboardingProgress.workflowsSetupOnboardingProgress)
  );
};

export const isHrDsarWorkflowsEnabled = (
  hrCenterOnboardingProgress?: TruevaultHrOnboardingProgress,
) => {
  if (!hrCenterOnboardingProgress) {
    return false;
  }
  let { isAllHrDataMapCompleted, isFinishedWithHrDsarWorkflows } = hrCenterOnboardingProgress;
  return isAllHrDataMapCompleted || isFinishedWithHrDsarWorkflows;
};

export const isHrNoticesEnabled = (hrCenterOnboardingProgress?: TruevaultHrOnboardingProgress) => {
  if (!hrCenterOnboardingProgress) {
    return false;
  }
  let { isAllHrDataMapCompleted, isFinishedWithHrDsarWorkflows, isHrNoticesPublished } =
    hrCenterOnboardingProgress;
  return (isAllHrDataMapCompleted && isFinishedWithHrDsarWorkflows) || isHrNoticesPublished;
};

export const HRCenterDashboardPage = () => {
  const orgId = usePrimaryOrganizationId();

  const [onboardingProgress] = useOnboardingProgress(orgId);
  let {
    isAllHrDataMapCompleted = false,
    isHrDataFlowsFinished = false,
    isFinishedWithHrDsarWorkflows = false,
  } = onboardingProgress?.hrCenterOnboardingProgress || {};
  const isHrNoticesComplete = areHrNoticesComplete(onboardingProgress?.hrCenterOnboardingProgress);

  let isHrDsarWorkflowsItemEnabled = isHrDsarWorkflowsEnabled(
    onboardingProgress?.hrCenterOnboardingProgress,
  );
  let isHrNoticesItemEnabled = isHrNoticesEnabled(onboardingProgress?.hrCenterOnboardingProgress);

  let hrDataMapItem = (
    <DashboardCompletionStepBox
      iconFilename="/assets/images/stay_compliant/hr_center/hr-retention-policy-icon.svg"
      title="HR Data Map"
      description="Review HR Vendors, collection groups and disclosures"
      status={isAllHrDataMapCompleted && isHrDataFlowsFinished ? "complete" : "finish-set-up"}
    />
  );

  let hrDsarWorkflowsItem = (
    <DashboardCompletionStepBox
      iconFilename="/assets/images/stay_compliant/hr_center/hr-retention-policy-icon.svg"
      title="HR DSAR Workflows"
      description="Review your request workflow settings"
      status={isFinishedWithHrDsarWorkflows ? "complete" : "finish-set-up"}
    />
  );

  let hrNoticesItem = (
    <DashboardCompletionStepBox
      iconFilename="/assets/images/stay_compliant/hr_center/hr-notices-icon.svg"
      title="Notices"
      description="Review your HR Notices and where to post them"
      status={isHrNoticesComplete ? "complete" : "finish-set-up"}
    />
  );

  return (
    <StandardPageWrapper fullWidth>
      <PageHeader titleContent="HR Center" />

      <div className="tw-boundary">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "16px",
            maxWidth: "830px",
            marginTop: "48px",
          }}
        >
          <a href={Routes.hrCenter.hrDataMap.root}>{hrDataMapItem}</a>

          {isHrDsarWorkflowsItemEnabled ? (
            <a href={Routes.hrCenter.hrDsarWorkflows}>{hrDsarWorkflowsItem}</a>
          ) : (
            cloneElement(hrDsarWorkflowsItem, { isDimmed: true })
          )}

          {isHrNoticesItemEnabled ? (
            <a href={Routes.hrCenter.hrNotices}>{hrNoticesItem}</a>
          ) : (
            cloneElement(hrNoticesItem, { isDimmed: true })
          )}
        </div>
      </div>
    </StandardPageWrapper>
  );
};
